/**
 * Canonical platform identifiers used across the entire application.
 * All processors must write these lowercase values to the database.
 * All dashboard/hook filters must match against these values.
 *
 * The `id` is the canonical DB value. `displayName` is for UI.
 * `aliases` lists legacy or variant strings that should resolve to this platform.
 */

export const PLATFORMS = {
  TOAST: 'toast',
  UBER: 'uber',
  DOORDASH: 'doordash',
  SQUARE: 'square',
  CLOVER: 'clover',
  REVEL: 'revel',
  TOUCHBISTRO: 'touchbistro',
  LIGHTSPEED: 'lightspeed',
  RESY: 'resy',
  OPENTABLE: 'opentable',
  GRUBHUB: 'grubhub',
  OTHER: 'other',
} as const;

export type PlatformId = typeof PLATFORMS[keyof typeof PLATFORMS];

export interface PlatformMeta {
  id: PlatformId;
  displayName: string;
  aliases: string[];
  hasFileProcessor: boolean;
}

export const PLATFORM_REGISTRY: PlatformMeta[] = [
  {
    id: PLATFORMS.TOAST,
    displayName: 'Toast',
    aliases: ['Toast', 'TOAST', 'Toast - Manual'],
    hasFileProcessor: true,
  },
  {
    id: PLATFORMS.UBER,
    displayName: 'Uber Eats',
    aliases: ['Uber Eats', 'uber eats', 'ubereats', 'uber_eats', 'UBER', 'Uber Eats - Manual'],
    hasFileProcessor: true,
  },
  {
    id: PLATFORMS.DOORDASH,
    displayName: 'DoorDash',
    aliases: ['DoorDash', 'Doordash', 'door dash', 'door_dash', 'DOORDASH', 'DoorDash - Manual'],
    hasFileProcessor: true,
  },
  {
    id: PLATFORMS.SQUARE,
    displayName: 'Square',
    aliases: ['Square', 'SQUARE'],
    hasFileProcessor: false,
  },
  {
    id: PLATFORMS.CLOVER,
    displayName: 'Clover',
    aliases: ['Clover', 'CLOVER'],
    hasFileProcessor: false,
  },
  {
    id: PLATFORMS.REVEL,
    displayName: 'Revel Systems',
    aliases: ['Revel Systems', 'revel systems', 'revel_systems', 'Revel', 'REVEL'],
    hasFileProcessor: false,
  },
  {
    id: PLATFORMS.TOUCHBISTRO,
    displayName: 'TouchBistro',
    aliases: ['TouchBistro', 'touch bistro', 'touch_bistro', 'TOUCHBISTRO'],
    hasFileProcessor: false,
  },
  {
    id: PLATFORMS.LIGHTSPEED,
    displayName: 'Lightspeed',
    aliases: ['Lightspeed', 'LIGHTSPEED'],
    hasFileProcessor: false,
  },
  {
    id: PLATFORMS.RESY,
    displayName: 'Resy',
    aliases: ['Resy', 'RESY'],
    hasFileProcessor: false,
  },
  {
    id: PLATFORMS.OPENTABLE,
    displayName: 'OpenTable',
    aliases: ['OpenTable', 'open table', 'open_table', 'OPENTABLE'],
    hasFileProcessor: false,
  },
  {
    id: PLATFORMS.GRUBHUB,
    displayName: 'Grubhub',
    aliases: ['Grubhub', 'grub hub', 'grub_hub', 'GrubHub', 'GRUBHUB'],
    hasFileProcessor: false,
  },
  {
    id: PLATFORMS.OTHER,
    displayName: 'Other',
    aliases: ['Other', 'other', 'OTHER'],
    hasFileProcessor: false,
  },
];

const aliasMap = new Map<string, PlatformId>();
for (const entry of PLATFORM_REGISTRY) {
  aliasMap.set(entry.id, entry.id);
  for (const alias of entry.aliases) {
    aliasMap.set(alias.toLowerCase(), entry.id);
  }
}

/**
 * Resolve any platform string (legacy, title-cased, integration type, etc.)
 * to its canonical lowercase PlatformId. Returns `'other'` for unknown values.
 */
export function normalizePlatform(raw: string | null | undefined): PlatformId {
  if (!raw) return PLATFORMS.OTHER;
  const key = raw.toLowerCase().trim();
  // Try exact match first, then strip common suffixes like " - manual", " - auto", " - auto sync"
  return aliasMap.get(key)
    ?? aliasMap.get(key.replace(/ - .+$/, ''))
    ?? PLATFORMS.OTHER;
}

/**
 * Get the user-facing display name for a platform identifier.
 */
export function platformDisplayName(id: string | null | undefined): string {
  const canonical = normalizePlatform(id);
  const entry = PLATFORM_REGISTRY.find(p => p.id === canonical);
  return entry?.displayName ?? 'Other';
}
